import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import logger from '../../utils/logger';
import { saveUserRating, getUserRating } from '../../api/api';
import CustomAlert from '../CustomAlert';

const EnergyMeterPrompt = ({
  processId,
  activityId,
  activityTitle,
  version,
  onClose,
  onSubmitted,
  style = {},
}) => {
  const promptRef = useRef(null);
  const t = useTranslation();
  const tr = (key, fallback) => {
    const val = t(key);
    if (val && val !== key) return val;
    if (fallback !== undefined) return fallback;
    return key;
  };
  const [selectedVote, setSelectedVote] = useState(null);
  const [reason, setReason] = useState('');
  const [showReasonField, setShowReasonField] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const fetchExisting = async () => {
      try {
        setIsLoading(true);
        const res = await getUserRating(processId, activityId, version);
        if (isMounted && res && res.rated) {
          setSelectedVote(res.vote);
          if (res.reason) {
            setReason(res.reason);
            setShowReasonField(true);
          }
        }
      } catch (err) {
        logger.error('Failed to fetch user rating:', err);
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    fetchExisting();
    return () => {
      isMounted = false;
    };
  }, [processId, activityId, version]);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (promptRef.current && promptRef.current.contains(e.target)) {
        return;
      }
      const nodeEl = e.target.closest('.react-flow__node');
      if (nodeEl) {
        const nodeId = nodeEl.getAttribute('data-id');
        if (nodeId && String(nodeId) === String(activityId)) {
          return;
        }
      }
      onClose();
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [activityId, onClose]);

  const handleVoteSelect = (vote) => {
    if (selectedVote === vote) {
      setSelectedVote(null);
    } else {
      setSelectedVote(vote);
    }
  };

  const handleSubmit = async () => {
    if (!selectedVote || isSubmitting) return;
    try {
      setIsSubmitting(true);
      await saveUserRating(processId, activityId, selectedVote, reason.trim() || null, version);
      if (onSubmitted) onSubmitted();
      onClose();
    } catch (err) {
      logger.error('Error submitting rating:', err);
      CustomAlert.error(
        t('error') || 'Error',
        t('failed_to_save_energy_rating') || 'Failed to save energy rating'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      ref={promptRef}
      className="em-prompt"
      role="dialog"
      aria-label="Energy meter rating prompt"
      style={{
        position: 'absolute',
        zIndex: 1050,
        ...style,
      }}
    >
      <div className="em-prompt__eyebrow">{tr('ENERGY_METER', 'ENERGY METER')}</div>
      <div className="em-prompt__title">{activityTitle || 'Step'}</div>
      <div className="em-prompt__sub">
        {tr('EM_PROMPT_SUB', 'Rate how energizing or draining this step is.')}
      </div>

      <div className="em-prompt__options">
        <button
          className="em-option em-option--e"
          type="button"
          aria-pressed={selectedVote === 'e'}
          onClick={() => handleVoteSelect('e')}
        >
          <span className="em-option__icon">
            <i className="fas fa-battery-full"></i>
          </span>
          <span className="em-option__body">
            <span className="em-option__label">{tr('ENERGIZING', 'Energizing')}</span>
            <span className="em-option__sub">{tr('EM_E_SUB', 'Boosts focus & productivity')}</span>
          </span>
          {selectedVote === 'e' && <i className="fas fa-check em-option__check"></i>}
        </button>

        <button
          className="em-option em-option--n"
          type="button"
          aria-pressed={selectedVote === 'n'}
          onClick={() => handleVoteSelect('n')}
        >
          <span className="em-option__icon">
            <i className="fas fa-battery-half"></i>
          </span>
          <span className="em-option__body">
            <span className="em-option__label">{tr('NEUTRAL', 'Neutral')}</span>
            <span className="em-option__sub">{tr('EM_N_SUB', 'Standard effort, no friction')}</span>
          </span>
          {selectedVote === 'n' && <i className="fas fa-check em-option__check"></i>}
        </button>

        <button
          className="em-option em-option--d"
          type="button"
          aria-pressed={selectedVote === 'd'}
          onClick={() => handleVoteSelect('d')}
        >
          <span className="em-option__icon">
            <i className="fas fa-battery-quarter"></i>
          </span>
          <span className="em-option__body">
            <span className="em-option__label">{tr('DRAINING', 'Draining')}</span>
            <span className="em-option__sub">
              {tr('EM_D_SUB', 'Frustrating, slow, or tedious')}
            </span>
          </span>
          {selectedVote === 'd' && <i className="fas fa-check em-option__check"></i>}
        </button>
      </div>

      {!showReasonField ? (
        <button
          type="button"
          className="em-prompt__why-toggle"
          onClick={() => setShowReasonField(true)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
        >
          {tr('ADD_SHORT_REASON', '+ Add short reason (optional)')}
        </button>
      ) : (
        <div className="em-prompt__why">
          <label className="em-prompt__why-label">{tr('WHY_LABEL', 'WHY?')}</label>
          <textarea
            maxLength={280}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={tr('WHY_PLACEHOLDER', 'What makes this step energizing or draining?')}
          />
          <div className="em-prompt__why-meta">
            <span>{tr('ANONYMOUS', 'Anonymous')}</span>
            <span>{reason.length} / 280</span>
          </div>
        </div>
      )}

      <div className="em-prompt__actions">
        <button className="em-btn em-btn--secondary" type="button" onClick={onClose}>
          {tr('CANCEL', 'Cancel')}
        </button>
        <button
          className="em-btn em-btn--primary"
          type="button"
          disabled={!selectedVote || isSubmitting || isLoading}
          onClick={handleSubmit}
          style={{ opacity: selectedVote && !isSubmitting ? 1 : 0.6 }}
        >
          {isSubmitting ? '...' : tr('SUBMIT', 'Submit')}
        </button>
      </div>
    </div>
  );
};

export default EnergyMeterPrompt;
